// The office sign-off on a renewal visit-frequency change (Session AIK, Director decision 3).
//
// A cadence change a customer asks for while renewing does not take effect on signature: it waits
// here. That restores, for the renewal surface, what Session AAO decided for the quote accept page,
// because more or fewer visits a year is a commitment to staff a different pattern and the office is
// the one who knows whether it can.
//
// WHY THE OFFICE MUST TYPE THE PRICE. A renewal's price is the customer's existing annual charge
// uplifted by CPI plus their contract's own rate, and escalation knows nothing about visit frequency.
// So there is no engine answer to fall back on that would be honest here: moving quarterly to monthly
// genuinely costs more, and only the office can say how much. The panel therefore asks for the figure
// rather than proposing one, and says plainly what happens if it is left blank.
//
// THE PANEL RENDERS NOTHING when no change was asked for, which is the overwhelmingly common case.
import { useCallback, useEffect, useState } from "react";
import { api, ApiError, type RenewalFrequencyReview } from "../../lib/api";
import { buttonClasses } from "../../components/Button";
import { ConfirmButton } from "../../components/ConfirmButton";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";
import { COST_FLOOR_TONE } from "../../lib/costFloorTone";

function fmtMoney(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}

export function RenewalFrequencyPanel({ contractId }: { contractId: string }) {
  const [review, setReview] = useState<RenewalFrequencyReview | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [annual, setAnnual] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReview(await api.contracts.renewalFrequency(contractId));
    } catch {
      // A read failure must not break the contract page.
      setReview(null);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = useCallback(
    async (frequency: string, withAnnual: boolean) => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        const parsed = Number(annual.replace(/[^0-9.]/g, ""));
        const res = await api.contracts.confirmRenewalFrequency(contractId, {
          frequency,
          ...(withAnnual && Number.isFinite(parsed) && parsed > 0 ? { annualValue: parsed } : {}),
        });
        setReview(res.review);
        setNotice(
          res.applied
            ? `Visit frequency changed. The renewed contract now runs ${res.frequency.replace("_", " ")} at £${Math.round(res.annualValue).toLocaleString("en-GB")} a year plus VAT.`
            : "Recorded: the visit frequency is unchanged. Let the customer know.",
        );
      } catch (err) {
        setError(
          err instanceof ApiError && typeof err.code === "string" && err.code.includes(" ")
            ? err.code
            : "Could not record that decision. Please try again.",
        );
      } finally {
        setBusy(false);
      }
    },
    [annual, contractId],
  );

  if (loading || !review || review.state === "none") return null;

  return (
    <section className="ele-card mt-4 p-4">
      {/* Session AQW: h2, matching RegisterConfirmationPanel and the other Renewal-tab cards --
          one heading level for every card title on the tab (was h3). */}
      <h2 className="font-heading text-lg text-ele-strong">Renewal visit frequency</h2>

      <p className="mt-2 text-sm text-ele-strong">
        The customer asked to move from <b>{review.currentFrequencyLabel}</b> to <b>{review.requestedFrequencyLabel}</b> visits.
      </p>
      <p className="mt-1 text-sm text-ele-body">{review.guidance}</p>

      {review.state === "confirmed" ? (
        <p className="mt-2 text-sm text-ele-body">
          Decided on {review.confirmedAt ? formatDate(review.confirmedAt) : ""}. The renewed contract runs {review.currentFrequencyLabel.toLowerCase()} at £
          {Math.round(review.currentAnnual).toLocaleString("en-GB")} a year plus VAT.
        </p>
      ) : null}

      {notice ? (
        <p role="status" className="mt-2 text-sm text-ele-green">
          {notice}
        </p>
      ) : null}
      {error ? (
        <p role="alert" className="mt-2 text-sm text-ele-danger">
          {error}
        </p>
      ) : null}

      {review.state === "ready" ? (
        <>
          {review.requestedCostFloorStatus ? (
            <div className="mt-3 rounded-ele border border-ele-hairline bg-ele-surface p-3">
              <StatusPill
                word={COST_FLOOR_TONE[review.requestedCostFloorStatus].word}
                tone={COST_FLOOR_TONE[review.requestedCostFloorStatus].tone}
              />
              <p className="mt-2 text-xs text-ele-body">
                If priced at list for this register, {review.requestedFrequencyLabel} visits would come to approximately{" "}
                {fmtMoney(review.requestedAnnualEstimate ?? 0)} a year, against an internal cost floor of{" "}
                {fmtMoney(review.requestedCostFloor ?? 0)}. Advisory only: it does not stop you confirming the change.
              </p>
            </div>
          ) : null}
          <label className="mt-3 block text-xs uppercase tracking-wide text-ele-body" htmlFor="renewal-frequency-annual">
            New annual charge, ex VAT
          </label>
          <input
            id="renewal-frequency-annual"
            inputMode="decimal"
            value={annual}
            onChange={(e) => setAnnual(e.target.value)}
            placeholder={String(Math.round(review.currentAnnual))}
            className="mt-1 w-40 rounded-ele border border-ele-hairline bg-ele-surface px-2 py-1.5 text-sm text-ele-strong"
          />
          <p className="mt-1 text-xs text-ele-body">
            More or fewer visits a year is a different annual charge, and a renewal price is an uplift of what the customer
            already paid rather than a calculator figure, so there is nothing to work it out from. Leave this blank and the
            charge stays at £{Math.round(review.currentAnnual).toLocaleString("en-GB")}.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <ConfirmButton
              onConfirm={() => void decide(review.requestedFrequency!, true)}
              busy={busy}
              busyLabel="Saving..."
              label={`Confirm ${review.requestedFrequencyLabel} visits`}
              confirmLabel="Press again to confirm"
              className={buttonClasses("primary", "md")}
              armedClassName="font-semibold ring-2 ring-ele-blue"
            />
            <ConfirmButton
              onConfirm={() => void decide(review.currentFrequency, false)}
              busy={busy}
              busyLabel="Saving..."
              label={`Keep ${review.currentFrequencyLabel} visits`}
              confirmLabel="Press again to keep"
              className="max-sm:min-h-11 rounded-ele border border-ele-hairline px-4 py-2 font-menu text-xs tracking-wide text-ele-strong transition hover:opacity-90 disabled:opacity-50"
              armedClassName="font-semibold ring-2 ring-ele-blue"
            />
          </div>
          <p className="mt-2 text-xs text-ele-body">
            Nothing is sent to the customer either way. Tell them what you decided.
          </p>
        </>
      ) : null}
    </section>
  );
}
